import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Field } from "./shiftUi";
import { NotifyCheckboxes } from "./NotifyCheckboxes";

export function ShiftDialogs({
  dupDlg, setDupDlg, doRepeatCreate, saving,
  scopeDlg, setScopeDlg, doUpdate,
  scopeHasWorker, scopeWorkerName, includeWorker, setIncludeWorker,
  delSeriesDlg, setDelSeriesDlg, doDeleteSeries,
  cancelScopeDlg, setCancelScopeDlg, doCancelScope, seriesMeta, openReason,
  reasonDlg, setReasonDlg, reason, setReason, confirmReason,
  cancelCharge, setCancelCharge,
  cancelInitiator, setCancelInitiator,
  cancelNotify, setCancelNotify,
  cloneDlg, setCloneDlg, doClone, cloning,
}) {
  const [cancelReason, setCancelReason] = useState("");
  const [cancelWithCharge, setCancelWithCharge] = useState(false);
  const [cancelFromDate, setCancelFromDate] = useState("");
  const [cancelScopeInitiator, setCancelScopeInitiator] = useState("");
  return (
    <>
      {/* Clone shift to another day */}
      <Dialog open={!!cloneDlg} onOpenChange={(v) => !v && setCloneDlg(null)}>
        <DialogContent data-testid="clone-dialog">
          <DialogHeader><DialogTitle className="text-base">Clone shift to another day</DialogTitle></DialogHeader>
          <p className="text-xs text-slate-500 -mt-2">Creates a new Draft shift on the chosen date, reusing all participants, billing, workers, times, location and sleepover.</p>
          <Field label="Target date">
            <Input type="date" data-testid="clone-date" value={cloneDlg?.date || ""}
              onChange={(e) => setCloneDlg((s) => ({ ...s, date: e.target.value }))} className="h-9 text-xs" />
          </Field>
          <DialogFooter>
            <Button variant="outline" size="sm" onClick={() => setCloneDlg(null)}>Cancel</Button>
            <Button size="sm" style={{ backgroundColor: "#bf3b4f" }} onClick={doClone} disabled={cloning} data-testid="clone-confirm">
              {cloning ? "Cloning…" : "Clone shift"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Duplicate warning dialog (repeating shifts) */}
      <Dialog open={!!dupDlg} onOpenChange={(v) => !v && setDupDlg(null)}>
        <DialogContent data-testid="duplicate-dialog">
          <DialogHeader><DialogTitle className="text-base">Some dates already have a matching shift</DialogTitle></DialogHeader>
          <p className="text-xs text-slate-500 -mt-2">{dupDlg?.duplicates?.length} of {dupDlg?.total} date(s) already have a shift with the same participant(s) and times.</p>
          <div className="max-h-32 overflow-auto text-[11px] text-slate-600 border rounded-md p-2">
            {dupDlg?.duplicates?.map((d, i) => <div key={i} data-testid="dup-date">{d}</div>)}
          </div>
          <DialogFooter>
            <Button variant="outline" size="sm" onClick={() => setDupDlg(null)}>Cancel</Button>
            <Button variant="outline" size="sm" onClick={() => doRepeatCreate("skip")} disabled={saving} data-testid="dup-skip">Skip duplicates</Button>
            <Button size="sm" style={{ backgroundColor: "#bf3b4f" }} onClick={() => doRepeatCreate("create")} disabled={saving} data-testid="dup-create">Create anyway</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit scope dialog (repeating shifts) */}
      <Dialog open={scopeDlg} onOpenChange={setScopeDlg}>
        <DialogContent data-testid="scope-dialog">
          <DialogHeader><DialogTitle className="text-base">Apply changes to…</DialogTitle></DialogHeader>
          <p className="text-xs text-slate-500 -mt-2">This shift is part of a repeating series. "This &amp; all future" updates this shift and every <strong>upcoming</strong> repeat — past shifts are never changed. Each future repeat keeps its own date.</p>
          {scopeHasWorker && (
            <label className="flex items-start gap-2 mt-1 rounded-md border border-slate-200 bg-slate-50 p-2.5 cursor-pointer" data-testid="scope-include-worker-row">
              <input type="checkbox" className="mt-0.5 accent-[#bf3b4f]" checked={!!includeWorker}
                onChange={(e) => setIncludeWorker(e.target.checked)} data-testid="scope-include-worker" />
              <span className="text-xs text-slate-600 leading-snug">
                Also assign <strong>{scopeWorkerName || "this worker"}</strong> to all future repeats.
                <span className="block text-[11px] text-slate-400 mt-0.5">Off by default — staff are normally rostered from the template, not the recurring edit. Each future shift keeps its current worker unless you tick this.</span>
              </span>
            </label>
          )}
          <DialogFooter>
            <Button variant="outline" size="sm" onClick={() => setScopeDlg(false)}>Cancel</Button>
            <Button variant="outline" size="sm" onClick={() => doUpdate("this")} disabled={saving} data-testid="scope-this">This shift only</Button>
            <Button size="sm" style={{ backgroundColor: "#bf3b4f" }} onClick={() => doUpdate("future", includeWorker)} disabled={saving} data-testid="scope-future">This &amp; all future</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete repeating series dialog */}
      <Dialog open={!!delSeriesDlg} onOpenChange={(v) => !v && setDelSeriesDlg(null)}>
        <DialogContent data-testid="delete-series-dialog">
          <DialogHeader><DialogTitle className="text-base">Delete repeating shifts</DialogTitle></DialogHeader>
          <p className="text-xs text-slate-500 -mt-2">Removes upcoming shifts in this series from the chosen date onward. Past shifts are never removed.</p>
          <Field label="Delete from date">
            <Input type="date" data-testid="delete-series-from" value={delSeriesDlg?.from || ""}
              onChange={(e) => setDelSeriesDlg((s) => ({ ...s, from: e.target.value }))} className="h-9 text-xs" />
          </Field>
          <DialogFooter>
            <Button variant="outline" size="sm" onClick={() => setDelSeriesDlg(null)}>Cancel</Button>
            <Button size="sm" className="bg-red-600 hover:bg-red-700" onClick={() => doDeleteSeries(delSeriesDlg.from)} data-testid="delete-series-confirm">Delete</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reason dialog */}
      <Dialog open={!!reasonDlg} onOpenChange={(v) => !v && setReasonDlg(null)}>
        <DialogContent data-testid="reason-dialog">
          <DialogHeader><DialogTitle className="text-base">{reasonDlg?.title} — reason required</DialogTitle></DialogHeader>
          <p className="text-xs text-slate-500 -mt-2">This reason is captured in the audit trail.</p>
          <Textarea data-testid="reason-input" value={reason} onChange={(e) => setReason(e.target.value)}
            placeholder="Enter mandatory reason…" className="text-sm min-h-[90px]" />
          {reasonDlg?.action === "cancel" && (
            <select
              data-testid="cancel-initiator-select"
              value={cancelInitiator}
              onChange={(e) => setCancelInitiator(e.target.value)}
              className="w-full rounded-md border border-slate-200 text-sm px-3 py-2 bg-white text-slate-700 focus:outline-none focus:ring-2 focus:ring-slate-300"
            >
              <option value="">— Who initiated this cancellation? —</option>
              <option value="Participant initiated">Participant initiated</option>
              <option value="Office initiated">Office initiated</option>
              <option value="Mutual agreement">Mutual agreement</option>
              <option value="Other">Other</option>
            </select>
          )}
          {reasonDlg?.action === "cancel" && (
            <label className="flex items-start gap-2 rounded-md border border-slate-200 px-3 py-2 cursor-pointer" data-testid="cancel-charge-toggle">
              <input type="checkbox" checked={!!cancelCharge} onChange={(e) => setCancelCharge(e.target.checked)}
                className="h-4 w-4 mt-0.5 accent-purple-600" data-testid="cancel-charge-checkbox" />
              <span className="text-xs text-slate-700">Still bill this cancellation <span className="text-slate-400">(short-notice charge — shows purple on the board; leave off for no charge / grey)</span></span>
            </label>
          )}
          {reasonDlg?.action === "cancel" && cancelNotify && (
            <NotifyCheckboxes testid="cancel-notify" value={cancelNotify} onChange={setCancelNotify}
              labels={["Notify staff of cancellation", "Notify participant of cancellation"]}
              hint="The cancellation reason above is included in the email." />
          )}
          <DialogFooter>
            <Button variant="outline" size="sm" onClick={() => setReasonDlg(null)}>Cancel</Button>
            <Button size="sm" style={{ backgroundColor: "#bf3b4f" }} onClick={confirmReason}
              disabled={!reason.trim() || (reasonDlg?.action === "cancel" && !cancelInitiator)}
              data-testid="reason-confirm">Confirm</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Partial cancel scope dialog (recurring series) */}
      <Dialog open={!!cancelScopeDlg} onOpenChange={(v) => { if (!v) { setCancelScopeDlg(false); setCancelReason(""); setCancelWithCharge(false); setCancelFromDate(""); setCancelScopeInitiator(""); } }}>
        <DialogContent data-testid="cancel-scope-dialog">
          <DialogHeader><DialogTitle className="text-base">Cancel recurring shift — choose scope</DialogTitle></DialogHeader>
          {seriesMeta && (
            <p className="text-xs text-slate-500 -mt-2">
              This shift is part of a series with <strong>{seriesMeta.total_count}</strong> occurrence{seriesMeta.total_count === 1 ? "" : "s"}
              {seriesMeta.upcoming_count > 0 ? ` (${seriesMeta.upcoming_count} upcoming)` : ""}.
              {seriesMeta.freq_label && <span className="block mt-0.5 font-medium text-slate-600">{seriesMeta.freq_label}</span>}
            </p>
          )}
          <Textarea
            data-testid="cancel-scope-reason"
            value={cancelReason}
            onChange={(e) => setCancelReason(e.target.value)}
            placeholder="Cancellation reason (mandatory)…"
            className="text-sm min-h-[80px]"
          />
          <select
            data-testid="cancel-scope-initiator-select"
            value={cancelScopeInitiator}
            onChange={(e) => setCancelScopeInitiator(e.target.value)}
            className="w-full rounded-md border border-slate-200 text-sm px-3 py-2 bg-white text-slate-700 focus:outline-none focus:ring-2 focus:ring-slate-300"
          >
            <option value="">— Who initiated this cancellation? —</option>
            <option value="Participant initiated">Participant initiated</option>
            <option value="Office initiated">Office initiated</option>
            <option value="Mutual agreement">Mutual agreement</option>
            <option value="Other">Other</option>
          </select>
          <Field label="Cancel from date (for 'This & all future')">
            <Input
              type="date"
              data-testid="cancel-scope-from-date"
              value={cancelFromDate}
              onChange={(e) => setCancelFromDate(e.target.value)}
              className="h-9 text-xs"
            />
            <p className="text-[10px] text-slate-400 mt-1">Leave blank to use the current shift's date. Only applies to the "This &amp; all future" option — past shifts are never removed.</p>
          </Field>
          <label className="flex items-start gap-2 rounded-md border border-slate-200 px-3 py-2 cursor-pointer" data-testid="cancel-scope-charge-toggle">
            <input type="checkbox" checked={cancelWithCharge} onChange={(e) => setCancelWithCharge(e.target.checked)}
              className="h-4 w-4 mt-0.5 accent-purple-600" data-testid="cancel-scope-charge-checkbox" />
            <span className="text-xs text-slate-700">Still bill this cancellation <span className="text-slate-400">(short-notice charge)</span></span>
          </label>
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button variant="outline" size="sm" onClick={() => { setCancelScopeDlg(false); setCancelReason(""); setCancelWithCharge(false); setCancelFromDate(""); setCancelScopeInitiator(""); }}>Back</Button>
            <Button variant="outline" size="sm" className="text-red-600 border-red-200 hover:bg-red-50"
              disabled={!cancelReason.trim() || !cancelScopeInitiator} data-testid="cancel-scope-this"
              onClick={() => { if (!cancelReason.trim() || !cancelScopeInitiator) return; doCancelScope("this", cancelReason.trim(), cancelWithCharge, null, cancelScopeInitiator); }}>
              This occurrence only
            </Button>
            <Button variant="outline" size="sm" className="text-red-600 border-red-200 hover:bg-red-50"
              disabled={!cancelReason.trim() || !cancelScopeInitiator} data-testid="cancel-scope-future"
              onClick={() => { if (!cancelReason.trim() || !cancelScopeInitiator) return; doCancelScope("future", cancelReason.trim(), cancelWithCharge, cancelFromDate || null, cancelScopeInitiator); }}>
              This &amp; all future
            </Button>
            <Button size="sm" className="bg-red-600 hover:bg-red-700 text-white"
              disabled={!cancelReason.trim() || !cancelScopeInitiator} data-testid="cancel-scope-all"
              onClick={() => { if (!cancelReason.trim() || !cancelScopeInitiator) return; doCancelScope("all", cancelReason.trim(), cancelWithCharge, null, cancelScopeInitiator); }}>
              Entire series
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
